import type { AdminInvoiceReportColumn } from '@/features/admin_invoice_reports/types';
import { useTranslation } from '@/i18n';
import {
  ModernPaginationProps,
  Table,
  TableProps,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useMemo } from 'react';
import { getTotalTableWidth, useColumns } from './Columns';
import { useResizableColumns } from './hooks/useResizableColumns';
import { useTableHeight } from './hooks/useTableHeight';
import styles from './Table.module.scss';
import {
  isValidSortState,
  MultiColumnSortState,
  SortOrderState,
} from './types';

const cx = classnames.bind(styles);

// Stable default constants — prevent new function/array references on each render
const EMPTY_SELECTED_ROW_KEYS: string[] = [];
const EMPTY_SELECT_ALL = () => {};
const EMPTY_SELECT_ROW = () => {};

type InternalTableProps = Omit<
  TableProps<AdminInvoiceReportColumn>,
  'columns' | 'pagination' | 'emptyType'
>;

type HandleSelectRowProps = {
  handleSelectAll?: (checked: boolean) => void;
  isAllSelected?: boolean;
  selectedRowKeys?: string[];
  handleSelectRow?: (
    record: AdminInvoiceReportColumn,
    checked: boolean
  ) => void;
  isAllDisabled?: boolean;
};

export type AdminInvoiceReportsTableProps = HandleSelectRowProps & {
  data: AdminInvoiceReportColumn[];
  pagination: ModernPaginationProps;
  isLoading?: boolean;
  isSearchEmpty?: boolean;
  sortOrders?: MultiColumnSortState[];
  onMultiColumnSort?: (sortStates: MultiColumnSortState[]) => void;
  onRowClick?: (id: string) => void;
  onCancel?: (record: AdminInvoiceReportColumn) => void;
  onDelete?: (record: AdminInvoiceReportColumn) => void;
};

const useEmptyState = (isSearchEmpty: boolean) => {
  const { t } = useTranslation();
  return useMemo(() => {
    if (isSearchEmpty) {
      return {
        emptyType: 'search' as const,
        emptyMessage: t('admin_invoice_reports.table.no_search_results'),
      };
    }
    return {
      emptyType: 'object' as const,
      emptyMessage: t('admin_invoice_reports.table.empty_message'),
    };
  }, [isSearchEmpty, t]);
};

export const AdminInvoiceReportsTable: FC<AdminInvoiceReportsTableProps> = memo(
  ({
    data,
    pagination,
    isLoading = false,
    isSearchEmpty = false,
    sortOrders,
    onMultiColumnSort,
    onRowClick,
    selectedRowKeys = EMPTY_SELECTED_ROW_KEYS,
    handleSelectRow = EMPTY_SELECT_ROW,
    handleSelectAll = EMPTY_SELECT_ALL,
    isAllSelected = false,
    isAllDisabled = false,
    onCancel,
    onDelete,
  }) => {
    const { columnWidths, handleResize } = useResizableColumns();

    const columns = useColumns({
      selectedRowKeys,
      onSelectAll: handleSelectAll,
      onSelectRow: handleSelectRow,
      isAllSelected,
      isAllDisabled,
      isLoading,
      columnWidths,
      onCancel,
      onDelete,
    });

    const { refContainer, listHeight } = useTableHeight({ marginHeight: 100 });

    const { emptyType, emptyMessage } = useEmptyState(isSearchEmpty);

    const handleColumnResize = useCallback(
      (columnIndex: number, width: number) => {
        handleResize(columnIndex, width, columns);
      },
      [handleResize, columns]
    );

    const resizableProps = useMemo(
      () => ({ resizable: true as const, onResize: handleColumnResize }),
      [handleColumnResize]
    );

    const totalTableWidth = useMemo(
      () => getTotalTableWidth(columnWidths),
      [columnWidths]
    );

    const scrollY = `${listHeight}px`;
    const scroll = useMemo(
      () => ({ x: totalTableWidth, y: scrollY }),
      [totalTableWidth, scrollY]
    );

    const handleMultiColumnSortWrapper = useCallback(
      (sortStates: unknown) => {
        if (!onMultiColumnSort) return;
        const statesArray = Array.isArray(sortStates)
          ? sortStates
          : [sortStates];
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const typedStates = statesArray as SortOrderState[];
        const converted: MultiColumnSortState[] = typedStates
          .filter(isValidSortState)
          .map((s) => ({ columnKey: s.columnKey, order: s.order }));
        onMultiColumnSort(converted);
      },
      [onMultiColumnSort]
    );

    const tableData: InternalTableProps = useMemo(
      () => ({
        data,
        onRow: (record) => ({
          onClick: () => onRowClick?.(record.id),
          className: selectedRowKeys.includes(record.id)
            ? cx('selected-row')
            : undefined,
        }),
      }),
      [data, onRowClick, selectedRowKeys]
    );

    const hasData = data.length > 0;
    const containerClassName = cx(
      styles['container'],
      hasData && styles['reports-has-data']
    );

    return (
      <div
        className={containerClassName}
        ref={refContainer}
        data-testid='admin-invoice-reports-table'
      >
        <Table
          emptyType={emptyType}
          {...tableData}
          legacy={false}
          key={pagination.currentPage}
          testId='admin-invoice-reports-table'
          emptyText={emptyMessage}
          columns={columns}
          pagination={pagination}
          hideHeaderPagination
          scroll={scroll}
          loading={isLoading}
          tableLayout='fixed'
          isMultiColumnSortEnabled
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
          onMultiColumnSort={handleMultiColumnSortWrapper as any}
          initialSortState={sortOrders}
          {...resizableProps}
        />
      </div>
    );
  }
);

AdminInvoiceReportsTable.displayName = 'AdminInvoiceReportsTable';
